import { Outlet, useNavigate, useOutletContext } from 'react-router-dom'

import { Box } from '@factorialco/deprecated-design-system'
import { OneDataCollection } from '@factorialco/f0-react/dist/experimental'
import {
  DollarBill,
  Download,
  Organization,
  Plane,
  Plus,
  Suitcase,
} from '@factorialco/f0-react/icons/app'
import { ExpensablesDataCollectionDocument } from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useGraphqlDataCollectionSource } from 'modules/core/hooks/useGraphqlDataCollectionSource'
import i18n from 'modules/core/lib/i18n'

import { mapFiltersToVariables } from 'modules/expenses/components/expenses/DataCollection/filters/filterUtils'
import { useDataCollectionConfig } from 'modules/expenses/components/expenses/DataCollection/useDataCollectionConfig'
import { useExpensablesStatusCounts } from 'modules/expenses/components/expenses/DataCollection/useExpensablesStatusCounts'
import { useExpensesDataCollection } from 'modules/expenses/components/expenses/DataCollection/useExpensesDataCollection'
import { useExpensesNavigation } from 'modules/expenses/hooks/useExpensesNavigation'
import { usePageDateFilters } from 'modules/expenses/lib/PageDateFiltersContext'
import { type RouteContextParams } from 'modules/expenses/lib/RouteContextParams'
import { useMyExpenses } from 'modules/expenses/pages/my_expenses/MyExpensesContext'
import { usePageActions } from 'modules/expenses/pages/my_expenses/usePageActions'

import { useMyExpensesBulkActions } from './useMyExpensesBulkActions'
import { useMyExpensesFilters } from './useMyExpensesFilters'
import { useMyExpensesItemActions } from './useMyExpensesItemActions'

interface Props {
  companyId: number
  employeeId: number
  back: { href: string }
  canSeeTransactions: boolean
  hasReportOnBehalfFeature: boolean
  recommendationsEnabled?: boolean
}

export const MyExpensesDataCollection = ({
  companyId,
  employeeId,
  back,
  canSeeTransactions,
  hasReportOnBehalfFeature,
  recommendationsEnabled,
}: Props) => {
  const context = useOutletContext<RouteContextParams>()
  const navigate = useNavigate()
  const { navigation } = useExpensesNavigation()
  const {
    access: { locale },
  } = useCurrentSession()

  const { sortings, defaultSortings, columns } = useExpensesDataCollection({
    isMyExpenses: true,
    locale,
  })
  const { dateFilters, setPageDateFilters } = usePageDateFilters()
  const { featureAvailabilities, data: myExpensesData } = useMyExpenses()

  const canCreateExpensesForOthers =
    myExpensesData?.expenses?._policies?.expenseCreateExpensesForOthers?.can ?? false

  const filterDefinitions = useMyExpensesFilters({
    companyId,
    hasReportOnBehalfFeature,
    canCreateExpensesForOthers,
    recommendationsEnabled,
  })

  const statusCounts = useExpensablesStatusCounts({
    companyId,
    employeeIds: [employeeId],
    includeManualDrafts: true,
    from: dateFilters.startDate,
    to: dateFilters.endDate,
  })

  const { navigationFilters: navigationFiltersDefinitions, presets: presetsDefinitions } =
    useDataCollectionConfig({ includeDraft: true, statusCounts })

  const { actions } = usePageActions({
    isInMyExpensesSection: true,
    canUsePerDiems: featureAvailabilities.canUsePerDiems,
    canCreateExpenses: featureAvailabilities.canCreateExpenses,
    expensableGroupsEnabled: featureAvailabilities.expensableGroups,
  })

  const { bulkActions, handleBulkAction, handlers, Modals } = useMyExpensesBulkActions({
    expensableGroupsEnabled: featureAvailabilities.expensableGroups,
    employeeId,
  })

  const { getItemActions } = useMyExpensesItemActions({
    expensableGroupsEnabled: featureAvailabilities.expensableGroups,
    employeeId,
    handlers,
    navigationHandler: { navigate, navigation },
  })

  const dataSource = useGraphqlDataCollectionSource({
    document: ExpensablesDataCollectionDocument,
    mapData: (data) => data.expenses.expensablesConnection.nodes,
    mapVariables: ({ navigationFilters, sortings: mapSortings, filters }) => {
      const { status, employeeIds: ownerFilterIds, filter } = mapFiltersToVariables(filters)
      // Use Owner filter if set, otherwise default to current employee
      // Backend uses OR: employeeIds OR reporterIds (includes expenses reported on behalf of others)
      const targetEmployeeIds = ownerFilterIds?.length ? ownerFilterIds : [employeeId]

      return {
        companyId,
        companyIdString: companyId.toString(),
        employeeIds: targetEmployeeIds,
        reporterIds: targetEmployeeIds,
        sortOrder: mapSortings?.[0],
        includeManualDrafts: true,
        includeGrouped: true,
        status,
        filter,
        from: navigationFilters?.date?.value?.from,
        to: navigationFilters?.date?.value?.to,
      }
    },
    paginationType: 'infinite-scroll',
    perPage: 25,
    mapPagination: (data) => ({
      total: data.expenses.expensablesConnection.totalCount ?? 0,
      cursor: data.expenses.expensablesConnection.pageInfo.endCursor ?? undefined,
      hasNextPage: data.expenses.expensablesConnection.pageInfo.hasNextPage ?? false,
    }),
    navigationFilters: navigationFiltersDefinitions,
    filters: filterDefinitions,
    presets: presetsDefinitions,
    sortings,
    defaultSortings,
    itemUrl: ({ id }) => navigation.helpers.expensableDetail(id),
    itemActions: getItemActions,
    selectable: (item) => item.id,
    bulkActions,
    primaryActions: () => {
      if (!actions.primary || actions.primary.length === 0) {
        return undefined
      }

      return actions.primary.map((action) => {
        switch (action.key) {
          case 'new_expense':
            return {
              icon: Plus,
              label: i18n.t('expenses_v2.buttons.add_expense'),
              onClick: () => navigate(action.to),
            }
          case 'new_mileage':
            return {
              icon: Plane,
              label: i18n.t('expenses_v2.buttons.add_mileage'),
              onClick: () => navigate(action.to),
            }
          case 'new_per_diem':
            return {
              icon: Suitcase,
              label: i18n.t('expenses_v2.buttons.add_per_diem'),
              onClick: () => navigate(action.to),
            }
          case 'new_group':
            return {
              icon: Organization,
              label: i18n.t('expenses_v2.buttons.add_group'),
              onClick: () => navigate(action.to),
            }
          default:
            return {
              icon: DollarBill,
              label: action.label,
              onClick: () => navigate(action.to),
            }
        }
      })
    },
    secondaryActions: {
      expanded: 0,
      actions: () => [
        {
          label: i18n.t('expenses_v2.buttons.export_expenses'),
          icon: Download,
          onClick: () => {
            navigate(navigation.exporter.fullPath())
          },
        },
      ],
    },
  })

  return (
    <>
      <Modals />
      <Outlet context={{ ...context, back, canSeeTransactions, dataCollectionEnabled: true }} />
      <Box paddingX="s12" style={{ height: '100%', overflowY: 'auto', overflowX: 'hidden' }}>
        <OneDataCollection
          id="my-expenses/v1"
          storage={{
            features: ['filters', 'sortings'],
          }}
          fullHeight
          emptyStates={{
            'no-data': {
              title: i18n.t('expenses_v2.list.empty_state.no_expenses'),
              description: '',
            },
          }}
          source={dataSource}
          onBulkAction={handleBulkAction}
          visualizations={[
            {
              type: 'table',
              options: {
                allowColumnHiding: true,
                allowColumnReordering: true,
                columns,
              },
            },
          ]}
          onStateChange={(state) => {
            // Sync date filters with page-level state
            const { from, to } = state.navigationFilters?.date?.value || {}
            if (from && to) {
              if (
                dateFilters.startDate.getTime() !== from.getTime() ||
                dateFilters.endDate.getTime() !== to.getTime()
              ) {
                setPageDateFilters(from, to)
              }
            }
          }}
        />
      </Box>
    </>
  )
}
